import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'

/**
 * Full-screen photo carousel — swipe (scroll-snap) or arrows; nothing
 * navigates away. Born in the Session Log (2026-08-01), promoted to
 * components/ when the Suggestion Box grew photos (2026-08-08).
 * `photos`: [{id, url, caption?}].
 */
export default function Carousel({ photos, start, onClose }) {
  const stripRef = useRef(null)
  const [idx, setIdx] = useState(start)

  useEffect(() => {
    const el = stripRef.current
    if (el) el.scrollTo({ left: start * el.clientWidth, behavior: 'instant' })
  }, [start])

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight') page(1)
      if (e.key === 'ArrowLeft') page(-1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  function page(dir) {
    const el = stripRef.current
    if (!el) return
    const target = Math.max(0, Math.min(photos.length - 1, Math.round(el.scrollLeft / el.clientWidth) + dir))
    // Instant jump: Chrome's snap-mandatory cancels smooth programmatic
    // scrolls, leaving the strip where it was. Swiping stays natively smooth.
    el.scrollTo({ left: target * el.clientWidth })
    setIdx(target)
  }

  function onScroll() {
    const el = stripRef.current
    if (el) setIdx(Math.round(el.scrollLeft / el.clientWidth))
  }

  // Portaled to <body> like the login sheet and lightbox — fixed overlays
  // rendered inside the shell's scroll pane get clipped to it on iOS.
  return createPortal(
    <div className="fixed inset-0 z-50 bg-ink/95">
      <button
        onClick={onClose}
        className="tap absolute right-3 top-3 z-10 flex items-center justify-center text-white"
        aria-label="Close photos"
      >
        <X size={26} />
      </button>

      <div
        ref={stripRef}
        onScroll={onScroll}
        className="flex h-full snap-x snap-mandatory overflow-x-auto overscroll-contain"
        style={{ scrollbarWidth: 'none' }}
      >
        {photos.map((p) => (
          <figure
            key={p.id}
            className="flex h-full w-full flex-none snap-center flex-col items-center justify-center p-4"
            onClick={onClose}
          >
            <img
              src={p.url}
              alt={p.caption || ''}
              className="max-h-[82dvh] max-w-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            {p.caption && <figcaption className="mt-3 text-sm text-white/85">{p.caption}</figcaption>}
          </figure>
        ))}
      </div>

      {photos.length > 1 && (
        <>
          {idx > 0 && (
            <button onClick={() => page(-1)} className="tap absolute left-1 top-1/2 -translate-y-1/2 text-white/80" aria-label="Previous photo">
              <ChevronLeft size={30} />
            </button>
          )}
          {idx < photos.length - 1 && (
            <button onClick={() => page(1)} className="tap absolute right-1 top-1/2 -translate-y-1/2 text-white/80" aria-label="Next photo">
              <ChevronRight size={30} />
            </button>
          )}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs text-white/70">
            {idx + 1} / {photos.length}
          </div>
        </>
      )}
    </div>,
    document.body
  )
}
